/**
 * UUID-карточка — минималистичная компактная версия.
 *
 * Layout сверху вниз (только то что реально нужно админу):
 *  1. Header: title + actions (Включить/Отозвать, Reset, Продлить)
 *  2. Compact info bar — юзер, тариф, статус, срок (одной строкой)
 *  3. CopyAccessButton — главный механизм передачи ключа получателю
 *  4. Live-статистика — TX / RX / Online / Total
 *  5. Trafic chart (30 дней) — визуализация
 *  6. Connection logs (24h) + Привязанные ноды (две колонки)
 *  7. Collapse «Дополнительно» — QR-форматы, sharing-флаги, метаданные
 *     (нужны редко, поэтому свёрнуты).
 */
import { Show } from "@refinedev/antd";
import { useShow, useCustomMutation, useInvalidate } from "@refinedev/core";
import {
  Card, Col, Row, Space, Table, Tag, Button, Statistic, Typography,
  Modal, InputNumber, Collapse, Descriptions, App as AntApp,
} from "antd";
import {
  StopOutlined, CheckCircleOutlined, ReloadOutlined, UnlockOutlined,
  StarFilled, StarOutlined, DisconnectOutlined,
  PlusCircleOutlined, GlobalOutlined,
  ArrowDownOutlined, ArrowUpOutlined,
  LinkOutlined, EditOutlined,
} from "@ant-design/icons";
import { Form, Input } from "antd";
import {
  Bar, BarChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis, Legend,
} from "recharts";
import { Link } from "react-router";
import { useState } from "react";
import { API_URL } from "../../dataProvider";
import { SubStatusTag } from "../../components/SubStatusTag";
import { SubUrlBlock } from "../../components/SubUrlBlock";
import { CopyAccessButton } from "../../components/CopyAccessButton";
import { DomainStats } from "../../components/DomainStats";
import { TARIFF_LABEL, TARIFF_COLOR, type SubscriptionDetail } from "../../types";
import { fmtBytes, fmtDateTime, fmtUserName, daysLeft } from "../../utils";

export const SubscriptionShow = () => {
  // App.useApp() hook — обязательно для antd v5 + React 19.
  //   • modal.confirm — static Modal.confirm() silently fails без контекста
  //     <App> wrapper'а (никакой ошибки, просто не открывается окно — именно
  //     поэтому юзер жаловался "нажимаю отвязать, ничего не происходит").
  //   • message     — в этом файле НЕ был импортирован из "antd" статически
  //     (см. import block выше); без хука вызов `message.error(...)` падал
  //     бы в ReferenceError, поэтому хук обязателен.
  const { modal, message } = AntApp.useApp();
  const { query } = useShow<SubscriptionDetail>();
  const { data, isLoading } = query;
  const sub = data?.data;

  const { mutate: callAction, isLoading: actionLoading } = useCustomMutation();
  const invalidate = useInvalidate();
  const [extending, setExtending] = useState<number | null>(null);
  const [bindingUrl, setBindingUrl] = useState<string | null>(null);
  const [bindingExpiresAt, setBindingExpiresAt] = useState<string | null>(null);
  const [bindingBusy, setBindingBusy] = useState(false);

  // Edit Modal — редактирование label/device_limit подписки + first_name/
  // last_name/tg_username/admin_notes юзера. Удобно для legacy-импортированных
  // (вместо «01-Windows-PC» в label — нормальное имя юзера).
  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editForm] = Form.useForm();
  const openEdit = () => {
    if (!sub) return;
    editForm.setFieldsValue({
      label: sub.label || "",
      device_limit: sub.device_limit,
      first_name: sub.first_name || "",
      last_name: sub.last_name || "",
      tg_username: sub.username || "",
    });
    setEditOpen(true);
  };
  const submitEdit = async () => {
    if (!sub) return;
    setEditBusy(true);
    try {
      const v = await editForm.validateFields();
      // 1. PATCH subscription (label, device_limit)
      const subPatch: Record<string, unknown> = {};
      if (v.label !== (sub.label || "")) subPatch.label = v.label || null;
      if (Number(v.device_limit) !== sub.device_limit)
        subPatch.device_limit = Number(v.device_limit);
      if (Object.keys(subPatch).length > 0) {
        const r1 = await fetch(`${API_URL}/admin/subscriptions/${sub.id}`, {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(subPatch),
        });
        if (!r1.ok) {
          message.error(`Ошибка PATCH subscription: ${(await r1.text()).slice(0, 200)}`);
          return;
        }
      }
      // 2. PATCH user (first_name, last_name, tg_username)
      const userPatch: Record<string, unknown> = {};
      if (v.first_name !== (sub.first_name || ""))
        userPatch.first_name = v.first_name || null;
      if (v.last_name !== (sub.last_name || ""))
        userPatch.last_name = v.last_name || null;
      if (v.tg_username !== (sub.username || ""))
        userPatch.tg_username = (v.tg_username || "").replace(/^@/, "") || null;
      if (Object.keys(userPatch).length > 0) {
        const r2 = await fetch(`${API_URL}/admin/users/${sub.user_id}`, {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(userPatch),
        });
        if (!r2.ok) {
          message.error(`Ошибка PATCH user: ${(await r2.text()).slice(0, 200)}`);
          return;
        }
      }
      message.success("Сохранено");
      setEditOpen(false);
      refresh();
    } catch (e) {
      message.error(`Ошибка: ${(e as Error).message}`);
    } finally {
      setEditBusy(false);
    }
  };

  const onClickBinding = async () => {
    if (!sub) return;
    setBindingBusy(true);
    try {
      const r = await fetch(`${API_URL}/admin/subscriptions/${sub.id}/binding-link`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: "{}",
      });
      if (!r.ok) {
        const t = await r.text().catch(() => r.statusText);
        message.error(`Не удалось создать ссылку: ${r.status} ${t.slice(0, 150)}`);
        return;
      }
      const data = await r.json();
      setBindingUrl(data.url);
      setBindingExpiresAt(data.expires_at);
    } catch (e) {
      message.error(`Сетевая ошибка: ${(e as Error).message}`);
    } finally {
      setBindingBusy(false);
    }
  };

  const refresh = () =>
    invalidate({ resource: "subscriptions", id: sub?.id, invalidates: ["detail"] });

  const action = (path: string, ok: string) => {
    if (!sub) return;
    callAction({
      url: `${API_URL}/admin/subscriptions/${sub.id}/${path}`,
      method: "post", values: {},
      successNotification: { message: ok, type: "success" },
    }, { onSuccess: refresh });
  };

  const [extendBusy, setExtendBusy] = useState(false);
  // Direct fetch (минуя useCustomMutation) — Refine v5 в этой сборке не
  // пробрасывает body для /extend → backend отдавал 422 Unprocessable.
  const submitExtend = async (days: number) => {
    if (!sub) return;
    setExtendBusy(true);
    try {
      const r = await fetch(`${API_URL}/admin/subscriptions/${sub.id}/extend`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days }),
      });
      if (!r.ok) {
        const txt = await r.text().catch(() => r.statusText);
        message.error(`Ошибка ${r.status}: ${txt.slice(0, 200)}`);
        return;
      }
      message.success(`Продлено на ${days} д.`);
      refresh();
      setExtending(null);
    } catch (e) {
      message.error(`Сетевая ошибка: ${(e as Error).message}`);
    } finally {
      setExtendBusy(false);
    }
  };

  // Toggle is_favorite через PATCH /admin/subscriptions/{id}.
  // Direct fetch — useCustomMutation в этой v5-сборке не пробрасывает body
  // (тот же баг что и /extend, см. submitExtend выше).
  const [favBusy, setFavBusy] = useState(false);
  const toggleFavorite = async () => {
    if (!sub) return;
    const next = !sub.is_favorite;
    setFavBusy(true);
    try {
      const r = await fetch(`${API_URL}/admin/subscriptions/${sub.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_favorite: next }),
      });
      if (!r.ok) {
        const txt = await r.text().catch(() => r.statusText);
        message.error(`Ошибка ${r.status}: ${txt.slice(0, 200)}`);
        return;
      }
      message.success(next
        ? "⭐ В избранном — sharing-блоки больше не применяются"
        : "Убрано из избранного");
      refresh();
    } catch (e) {
      message.error(`Сетевая ошибка: ${(e as Error).message}`);
    } finally {
      setFavBusy(false);
    }
  };

  // Отвязка TG: создаёт fake-юзера, переписывает subscriptions.user_id.
  // Юзер видит модал-confirm перед нажатием — это destructive операция.
  const [unbindBusy, setUnbindBusy] = useState(false);
  const onClickUnbind = () => {
    if (!sub) return;
    modal.confirm({
      title: `🔓 Отвязать TG от подписки #${sub.id}?`,
      content: (
        <div>
          <p>Текущий владелец (tg_id <code>{sub.user_id}</code>) будет
          отключён от подписки. Подписка станет «бесхозной» —
          привязана к fake-юзеру.</p>
          <p>Дальше сгенерируйте новую <b>ссылку для привязки</b> и
          отдайте её правильному человеку.</p>
        </div>
      ),
      okText: "Отвязать", okButtonProps: { danger: true },
      onOk: async () => {
        setUnbindBusy(true);
        try {
          const r = await fetch(`${API_URL}/admin/subscriptions/${sub.id}/unbind-tg`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
          });
          if (!r.ok) {
            const t = await r.text().catch(() => r.statusText);
            message.error(`Ошибка ${r.status}: ${t.slice(0, 200)}`);
            return;
          }
          const data = await r.json();
          message.success(`Отвязано от tg_id=${data.old_user_id}. Теперь сгенерируйте новую ссылку привязки.`);
          refresh();
        } catch (e) {
          message.error(`Сетевая ошибка: ${(e as Error).message}`);
        } finally {
          setUnbindBusy(false);
        }
      },
    });
  };

  if (!sub) return <Show isLoading={isLoading} />;

  // Hysteria stats: tx = bytes сервер→клиент (для юзера это «скачал» —
  // контент сайтов, видео, ответы API). rx = bytes клиент→сервер (для
  // юзера это «отправил» — запросы, формы, аплоады). Показываем с точки
  // зрения юзера: download / upload — это понятнее.
  const downloaded = sub.live?.tx_total ?? 0;
  const uploaded   = sub.live?.rx_total ?? 0;
  const onlineNow = sub.live?.online_now ?? 0;
  const onlineMin = sub.daily_traffic.reduce((s, d) => s + d.online_minutes, 0);
  const left = daysLeft(sub.end_at);

  return (
    <Show
      isLoading={isLoading}
      title={`${sub.is_favorite ? "⭐ " : ""}UUID #${sub.id}${sub.label ? ` · ${sub.label}` : ""}`}
      breadcrumb={false}
      headerButtons={() => (
        <Space>
          {sub.hysteria_revoked_at ? (
            <Button type="primary" icon={<CheckCircleOutlined />} loading={actionLoading}
                    onClick={() => action("unrevoke", "Включено")}>
              Включить
            </Button>
          ) : (
            <Button danger icon={<StopOutlined />} loading={actionLoading}
                    onClick={() => action("revoke", "Отозвано")}>
              Отозвать
            </Button>
          )}
          <Button icon={<ReloadOutlined />} loading={actionLoading}
                  onClick={() => action("reset_key", "Ключ сброшен")}>
            Сбросить ключ
          </Button>
          {sub.shared_block_until && new Date(sub.shared_block_until) > new Date() && (
            <Button type="primary" icon={<UnlockOutlined />} loading={actionLoading}
                    onClick={() => action("unblock-sharing", "Блок sharing снят")}
                    title="Снять автоматический блок, выставленный детектором sharing">
              Снять блок sharing
            </Button>
          )}
          <Button
            type={sub.is_favorite ? "primary" : "default"}
            icon={sub.is_favorite ? <StarFilled style={{ color: "#faad14" }} /> : <StarOutlined />}
            loading={favBusy}
            onClick={toggleFavorite}
            title={sub.is_favorite
              ? "Убрать из избранного — снова будут действовать sharing-проверки"
              : "В избранное — отключает sharing-детектор и любые soft-blocks"}
          >
            {sub.is_favorite ? "В избранном" : "В избранное"}
          </Button>
          <Button icon={<PlusCircleOutlined />}
                  onClick={() => setExtending(30)}>
            Продлить
          </Button>
          <Button icon={<LinkOutlined />} loading={bindingBusy}
                  onClick={onClickBinding}>
            Привязать TG
          </Button>
          <Button danger icon={<DisconnectOutlined />}
                  loading={unbindBusy}
                  onClick={onClickUnbind}
                  title="Отвязать текущего TG-владельца (создаёт fake-юзера). Используйте если по ссылке привязки кликнул не тот человек.">
            Отвязать TG
          </Button>
          <Button icon={<EditOutlined />} onClick={openEdit}>
            Редактировать
          </Button>
        </Space>
      )}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>

        {/* 1. Compact info bar — одна строка с самым важным */}
        <Card size="small">
          <Space size="middle" wrap split={<Typography.Text type="secondary">·</Typography.Text>}>
            <Space size={4}>
              <Typography.Text type="secondary">Юзер:</Typography.Text>
              <Link to={`/users/${sub.user_id}`}>
                <Typography.Text strong>{fmtUserName(sub)}</Typography.Text>
              </Link>
            </Space>
            <Space size={4}>
              <Typography.Text type="secondary">Тариф:</Typography.Text>
              <Tag color={TARIFF_COLOR[sub.tariff]}>{TARIFF_LABEL[sub.tariff]}</Tag>
            </Space>
            <Space size={4}>
              <Typography.Text type="secondary">Статус:</Typography.Text>
              <SubStatusTag s={{
                is_active: sub.is_active,
                hysteria_revoked_at: sub.hysteria_revoked_at,
                shared_block_until: sub.shared_block_until,
                end_at: sub.end_at,
              }} />
            </Space>
            <Space size={4}>
              <Typography.Text type="secondary">До:</Typography.Text>
              <Typography.Text>{fmtDateTime(sub.end_at)}</Typography.Text>
              {left !== null && (
                <Tag color={left <= 0 ? "red" : left <= 3 ? "orange" : left <= 7 ? "gold" : "default"}>
                  {left > 0 ? `${left} д.` : "истекла"}
                </Tag>
              )}
            </Space>
            {!sub.payment_id && <Tag color="cyan">тестовая</Tag>}
          </Space>
        </Card>

        {/* 2. Главный блок — кнопка копирования */}
        <CopyAccessButton subUrl={sub.sub_url} />

        {/* 3. Live-статистика трафика */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Сейчас онлайн" value={onlineNow}
                valueStyle={{ color: onlineNow > 0 ? "#52c41a" : undefined }}
                suffix={onlineNow > 0
                  ? <Tag color="green" style={{ marginLeft: 4, fontSize: 11 }}>активен</Tag>
                  : null}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Скачал" value={fmtBytes(downloaded)}
                prefix={<ArrowDownOutlined style={{ color: "#52c41a" }} />}
              />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                сайты, видео, ответы серверов
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Отправил" value={fmtBytes(uploaded)}
                prefix={<ArrowUpOutlined style={{ color: "#1677ff" }} />}
              />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                запросы, формы, файлы
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic title="Всего трафика"
                         value={fmtBytes(downloaded + uploaded)}
                         valueStyle={{ color: "#1677ff", fontWeight: 600 }} />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Время онлайн: {onlineMin} мин
              </Typography.Text>
            </Card>
          </Col>
        </Row>

        {/* 4. График трафика */}
        <Card title="Трафик за 30 дней" size="small">
          <div style={{ width: "100%", height: 240 }}>
            {sub.daily_traffic.length === 0 ? (
              <Typography.Text type="secondary">
                Данных пока нет — данные появятся через 5 минут после первого подключения.
              </Typography.Text>
            ) : (
              <ResponsiveContainer>
                <BarChart data={sub.daily_traffic}
                          margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <XAxis dataKey="day" tickFormatter={(v: string) => v.slice(5)} />
                  <YAxis tickFormatter={(v: number) => fmtBytes(v)} />
                  <RTooltip formatter={(v: number) => fmtBytes(v)} />
                  <Legend />
                  <Bar dataKey="tx_bytes" stackId="a" fill="#52c41a" name="Скачал" />
                  <Bar dataKey="rx_bytes" stackId="a" fill="#1677ff" name="Отправил" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* 5. Подключения + Ноды */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Card title={`Подключения за 24ч (${sub.connection_logs.length})`}
                  size="small">
              <Table size="small" pagination={{ pageSize: 8, hideOnSinglePage: true }}
                     rowKey="id" dataSource={sub.connection_logs}
                     locale={{ emptyText: "Подключений за сутки не было" }}
                     columns={[
                       { title: "Когда", dataIndex: "connected_at",
                         render: fmtDateTime },
                       { title: "IP", dataIndex: "ip",
                         render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
                       { title: "Город", dataIndex: "city",
                         render: (v: string | null) => v || "—" },
                       { title: "ASN", dataIndex: "asn",
                         render: (v: number | null) => v ?? "—" },
                     ]} />
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card title="Ноды" size="small">
              <Table size="small" pagination={false} rowKey="id"
                     dataSource={sub.nodes}
                     columns={[
                       { title: "Имя", dataIndex: "name" },
                       { title: "IP", dataIndex: "ip",
                         render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
                       { title: "Локация", dataIndex: "location",
                         render: (v: string) => <Space size={4}><GlobalOutlined />{v}</Space> },
                     ]} />
            </Card>
          </Col>
        </Row>

        {/* 6. Статистика посещённых сайтов — полная, с сортировкой/фильтром.
            Только домены + время + кол-во заходов (без IP, по требованию). */}
        <DomainStats subscriptionId={sub.id} />

        {/* 7. Дополнительно — свёрнуто, нужно редко */}
        <Collapse
          items={[
            {
              key: "qr",
              label: "🔑 Дополнительные форматы и QR-коды для других клиентов",
              children: <SubUrlBlock subUrl={sub.sub_url} />,
            },
            {
              key: "meta",
              label: "ⓘ Технические метаданные",
              children: (
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="ID подписки">
                    <Typography.Text code>#{sub.id}</Typography.Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Telegram ID">
                    <Typography.Text code>{sub.user_id}</Typography.Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Лимит устройств">
                    {sub.device_limit}
                  </Descriptions.Item>
                  <Descriptions.Item label="Sub-token">
                    <Typography.Text code copyable={{ text: sub.sub_token }}>
                      {sub.sub_token.slice(0, 8)}…
                    </Typography.Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Сбросов ключа">
                    {sub.uuid_reset_count}
                    {sub.last_uuid_reset_at &&
                      ` (последний ${fmtDateTime(sub.last_uuid_reset_at)})`}
                  </Descriptions.Item>
                  <Descriptions.Item label="Sharing-блокировки">
                    {sub.sharing_alerts_count > 0
                      ? <Tag color="volcano">{sub.sharing_alerts_count}</Tag>
                      : "нет"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Платёж">
                    {sub.payment_id
                      ? <Link to={`/payments?user_id=${sub.user_id}`}>
                          #{sub.payment_id}
                        </Link>
                      : "тестовая (без оплаты)"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Создана">
                    {fmtDateTime(sub.start_at)}
                  </Descriptions.Item>
                </Descriptions>
              ),
            },
          ]}
        />

      </Space>

      <Modal
        title={`Продлить UUID #${sub.id}`}
        open={extending !== null}
        onCancel={() => setExtending(null)}
        onOk={() => extending !== null && submitExtend(extending)}
        confirmLoading={extendBusy}
        okText="Продлить"
      >
        <p>На сколько дней продлить?</p>
        <InputNumber min={1} max={365}
                     value={extending ?? 30}
                     onChange={(v) => setExtending(Number(v) || 30)}
                     style={{ width: "100%" }} />
      </Modal>

      <Modal
        title="✏️ Редактировать подписку"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={submitEdit}
        confirmLoading={editBusy}
        okText="Сохранить"
        width={520}
      >
        <Form form={editForm} layout="vertical">
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Подписка #{sub.id}
          </Typography.Title>
          <Form.Item name="label" label="Ярлык (label)"
                     extra="Видно в списке подписок и в карточке. Для legacy — это email с прежнего сервера.">
            <Input placeholder="Например: Юрий Сафонов / iPhone Сони" maxLength={120} />
          </Form.Item>
          <Form.Item name="device_limit" label="Лимит устройств"
                     extra="Сколько устройств может одновременно подключаться. Превышение → soft-block через sharing-detect.">
            <InputNumber min={1} max={50} style={{ width: 160 }} />
          </Form.Item>

          <Typography.Title level={5}>
            Юзер tg:{sub.user_id}
          </Typography.Title>
          <Form.Item name="first_name" label="Имя">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item name="last_name" label="Фамилия">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item name="tg_username" label="@username Telegram"
                     extra="Без знака @ — будет добавлен автоматически.">
            <Input maxLength={80} placeholder="имя_без_собаки" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="🔗 Ссылка для привязки Telegram-аккаунта"
        open={bindingUrl !== null}
        onCancel={() => setBindingUrl(null)}
        footer={
          <Button type="primary" onClick={() => setBindingUrl(null)}>Закрыть</Button>
        }
        width={620}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Typography.Text>
            Отправьте эту ссылку клиенту. Когда он перейдёт по ней
            в Telegram, его аккаунт автоматически привяжется к этой подписке,
            и он сразу получит свой ключ.
          </Typography.Text>
          {bindingUrl && (
            <Typography.Paragraph
              copyable={{ text: bindingUrl, tooltips: ["Скопировать", "Скопировано"] }}
              style={{ marginBottom: 0 }}
            >
              <Typography.Text code style={{ fontSize: 13, wordBreak: "break-all" }}>
                {bindingUrl}
              </Typography.Text>
            </Typography.Paragraph>
          )}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            ⏰ Ссылка действительна до{" "}
            {bindingExpiresAt ? fmtDateTime(bindingExpiresAt) : "—"} (24 часа).
            Одноразовая — после первого использования перестаёт работать.
            При повторном нажатии «Привязать TG» — вернёт ту же ссылку,
            если она ещё не истекла и не использована.
          </Typography.Text>
        </Space>
      </Modal>
    </Show>
  );
};
